import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { 
  ArrowRightIcon,
  ArrowPathIcon,
  PencilIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  UserIcon,
  DocumentTextIcon,
  BuildingOfficeIcon,
  PrinterIcon,
  CogIcon,
  ClipboardDocumentListIcon,
  CloudArrowUpIcon,
  EyeIcon,
  XMarkIcon,
  XCircleIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  PlayCircleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import api from '../../services/api';
import axios from 'axios'; // TODO: rimuovere, usare api
import API_CONFIG from '../../config/api';
import ConfermaClienteConPreventivi from './ConfermaClienteConPreventivi';

const GestoreStatiPratica = forwardRef(({ 
  pratica, 
  ruoloUtente = 'agente', 
  onStatoAggiornato,
  isPraticaModificabileAgente = () => true
}, ref) => {
  const [loadingAvanzaStato, setLoadingAvanzaStato] = useState(false);
  const [showStateModal, setShowStateModal] = useState(false);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [showConfermaClienteModal, setShowConfermaClienteModal] = useState(false);
  const [showEsitoNegativoModal, setShowEsitoNegativoModal] = useState(false);
  const [motivazioneEsitoNegativo, setMotivazioneEsitoNegativo] = useState('');
  const [loadingEsitoNegativo, setLoadingEsitoNegativo] = useState(false);

  const getStatoInfo = (stato) => {
    const statiInfo = {
      // Workflow ufficiale
      'pratica_avviata': { 
        label: 'Pratica Avviata', 
        color: 'bg-blue-100 text-blue-800', 
        IconComp: PlayCircleIcon,
        iconColor: 'text-blue-600',
        descrizione: 'Pratica appena creata. Agenzia raccoglie informazioni cliente e preventiva.'
      },
      'attesa_preventivo': { 
        label: 'Attesa Preventivo', 
        color: 'bg-purple-100 text-purple-800', 
        IconComp: DocumentTextIcon,
        iconColor: 'text-purple-600',
        descrizione: 'Richiesta preventivo personalizzato inviata, in attesa di elaborazione amministratore.'
      },
      'valutazione_cliente': { 
        label: 'Valutazione Cliente', 
        color: 'bg-yellow-100 text-yellow-800', 
        IconComp: EyeIcon,
        iconColor: 'text-yellow-600',
        descrizione: 'Preventivo pronto. Agenzia deve presentarlo al cliente e confermare volonta di procedere.'
      },
      'attesa_documenti_cliente': { 
        label: 'Attesa Documenti Cliente', 
        color: 'bg-orange-100 text-orange-800', 
        IconComp: CloudArrowUpIcon,
        iconColor: 'text-orange-600',
        descrizione: 'Cliente ha accettato. Agenzia carica documenti richiesti.'
      },
      'richiesta_esito': { 
        label: 'Richiesta Esito', 
        color: 'bg-amber-100 text-amber-800', 
        IconComp: ClipboardDocumentListIcon,
        iconColor: 'text-amber-600',
        descrizione: 'Documenti inviati. Amministratore controlla e decide se procedere.'
      },
      'pratica_in_valutazione': { 
        label: 'Pratica in Valutazione', 
        color: 'bg-indigo-100 text-indigo-800', 
        IconComp: ShieldCheckIcon,
        iconColor: 'text-indigo-600',
        descrizione: 'Valutazione finale amministratore: approvazione, rifiuto o richiesta integrazione.'
      },
      'attesa_firma_contratto': { 
        label: 'Attesa Firma Contratto', 
        color: 'bg-green-100 text-green-800', 
        IconComp: PrinterIcon,
        iconColor: 'text-green-600',
        descrizione: 'Pratica approvata. Amministratore carica contratto, agenzia fa firmare al cliente.'
      },
      'contratto_attivo': { 
        label: 'Contratto Attivo', 
        color: 'bg-emerald-100 text-emerald-800', 
        IconComp: CheckCircleIcon,
        iconColor: 'text-emerald-600',
        descrizione: 'Contratto firmato e attivo. Pratica completata con successo.'
      },
      
      // Stati speciali
      'richiesta_integrazione': { 
        label: 'Richiesta Integrazione', 
        color: 'bg-red-100 text-red-800', 
        IconComp: ArrowPathIcon,
        iconColor: 'text-red-600',
        descrizione: 'Documenti mancanti o errati. Agenzia deve integrare documentazione.'
      },
      'pratica_chiusa': {
        label: 'Pratica Chiusa',
        color: 'bg-red-100 text-red-800',
        IconComp: XCircleIcon,
        iconColor: 'text-red-600',
        descrizione: 'Pratica rifiutata e chiusa definitivamente.'
      },
      'esito_negativo': {
        label: 'Esito Negativo',
        color: 'bg-rose-100 text-rose-800',
        IconComp: XCircleIcon,
        iconColor: 'text-rose-600',
        descrizione: 'Pratica chiusa con esito negativo (operazione non andata a buon fine).'
      },
      'condizionata': {
        label: 'Condizionata',
        color: 'bg-yellow-100 text-yellow-800',
        IconComp: ExclamationTriangleIcon,
        iconColor: 'text-yellow-600',
        descrizione: 'Approvazione condizionata: in attesa che le condizioni siano soddisfatte per proseguire o chiudere.'
      },

      // Stati legacy per compatibilita (nascosti dal cambio stato manuale)
      'attesa_approvazione_cliente': { 
        label: 'Attesa Approvazione Cliente', 
        color: 'bg-yellow-100 text-yellow-800', 
        IconComp: ClockIcon,
        iconColor: 'text-yellow-600',
        descrizione: 'Cliente deve valutare i preventivi (stato legacy).'
      }
    };
    
    return statiInfo[stato] || { 
      label: stato, 
      color: 'bg-gray-100 text-gray-800', 
      IconComp: InformationCircleIcon,
      iconColor: 'text-gray-600',
      descrizione: 'Stato non riconosciuto' 
    };
  };

  // Workflow aggiornato secondo le specifiche dell'utente
  const getWorkflowPerStato = () => {
    const statoCorrente = pratica.statoPratica || pratica.stato;
    
    switch (statoCorrente) {
      case 'pratica_avviata':
        if (ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale') {
          return {
            titolo: '1. Pratica Avviata - Workflow Agenzia',
            descrizione: 'Raccogliere informazioni cliente e iniziare processo preventivazione.',
            azioni: [
              {
                id: 'modifica_anagrafica',
                label: 'Modifica dati anagrafici del cliente',
                icon: UserIcon,
                obbligatoria: false,
                status: 'available'
              },
              {
                id: 'seleziona_offerta',
                label: 'Seleziona offerta compatibile con esigenze cliente',
                icon: CheckCircleIcon,
                obbligatoria: false,
                status: 'available'
              },
              {
                id: 'richiedi_preventivo_personalizzato',
                label: 'Richiedi preventivo personalizzato',
                icon: DocumentTextIcon,
                obbligatoria: false,
                status: 'available'
              }
            ]
          };
        } else if (ruoloUtente === 'admin') {
          return {
            titolo: '1. Pratica Avviata - Workflow Amministratore',
            descrizione: 'Supervisione e supporto alla pratica.',
            azioni: [
              {
                id: 'modifica_anagrafica',
                label: 'Modifica dati anagrafici cliente',
                icon: UserIcon,
                obbligatoria: false,
                status: 'available'
              },
              {
                id: 'riassegna_agenzia',
                label: 'Assegna lead ad altra agenzia',
                icon: BuildingOfficeIcon,
                obbligatoria: false,
                status: 'available'
              }
            ]
          };
        }
        break;

      case 'attesa_preventivo':
        if (ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale') {
          return {
            titolo: '2. Attesa Preventivo - Workflow Agenzia',
            descrizione: 'Preventivo personalizzato in elaborazione. Possibili modifiche fino a quando non viene lavorato.',
            azioni: [
              {
                id: 'modifica_richiesta',
                label: 'Apporta modifiche alla richiesta di preventivo',
                icon: PencilIcon,
                obbligatoria: false,
                status: 'available'
              },
              {
                id: 'richiedi_ulteriori',
                label: 'Richiedi ulteriori preventivi',
                icon: DocumentTextIcon,
                obbligatoria: false,
                status: 'available'
              }
            ]
          };
        } else if (ruoloUtente === 'admin') {
          return {
            titolo: '2. Attesa Preventivo - Workflow Amministratore',
            descrizione: 'Elabora richiesta preventivo personalizzato.',
            azioni: [
              {
                id: 'modifica_richiesta',
                label: 'Modifica la richiesta di preventivo',
                icon: PencilIcon,
                obbligatoria: false,
                status: 'available'
              },
              {
                id: 'elabora_preventivo',
                label: 'Elabora preventivo personalizzato',
                icon: CheckCircleIcon,
                obbligatoria: true,
                status: 'pending'
              }
            ]
          };
        }
        break;

      case 'valutazione_cliente':
        if (ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale') {
          return {
            titolo: '3. Valutazione Cliente - Workflow Agenzia',
            descrizione: 'Preventivo pronto. Presenta al cliente e conferma la volontà di procedere.',
            azioni: [
              {
                id: 'conferma_cliente',
                label: 'Conferma che il cliente vuole procedere',
                icon: CheckCircleIcon,
                obbligatoria: true,
                status: 'pending'
              },
              {
                id: 'carica_documenti_anticipati',
                label: 'Carica documenti (se già disponibili)',
                icon: CloudArrowUpIcon,
                obbligatoria: false,
                status: 'available'
              }
            ]
          };
        }
        break;

      case 'attesa_documenti_cliente':
        if (ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale') {
          return {
            titolo: '4. Attesa Documenti Cliente - Workflow Agenzia',
            descrizione: 'Cliente ha accettato. Carica documenti del cliente.',
            azioni: [
              {
                id: 'carica_documenti',
                label: 'Carica documenti del cliente',
                icon: CloudArrowUpIcon,
                obbligatoria: true,
                status: 'pending'
              },
              {
                id: 'modifica_documenti',
                label: 'Modifica documenti caricati per errore',
                icon: PencilIcon,
                obbligatoria: false,
                status: 'available'
              }
            ]
          };
        }
        break;

      case 'richiesta_esito':
        if (ruoloUtente === 'admin') {
          return {
            titolo: '5. Richiesta Esito - Workflow Amministratore',
            descrizione: 'Controlla documenti e decide se procedere o richiedere integrazioni.',
            azioni: [
              {
                id: 'controlla_documenti',
                label: 'Controlla che i documenti siano corretti',
                icon: EyeIcon,
                obbligatoria: true,
                status: 'pending'
              },
              {
                id: 'conferma_documenti',
                label: 'Conferma documenti corretti → Pratica in valutazione',
                icon: CheckCircleIcon,
                obbligatoria: false,
                status: 'available'
              },
              {
                id: 'richiedi_integrazione',
                label: 'Richiedi ulteriori documenti → Richiesta integrazione',
                icon: ExclamationTriangleIcon,
                obbligatoria: false,
                status: 'available'
              }
            ]
          };
        }
        break;

      case 'pratica_in_valutazione':
        if (ruoloUtente === 'admin') {
          return {
            titolo: '6. Pratica in Valutazione - Workflow Amministratore',
            descrizione: 'Valutazione finale: approva, rifiuta o richiedi integrazione.',
            azioni: [
              {
                id: 'approva_pratica',
                label: 'Approva pratica → Attesa firma contratto',
                icon: CheckCircleIcon,
                obbligatoria: false,
                status: 'available'
              },
              {
                id: 'rifiuta_pratica',
                label: 'Rifiuta pratica → Pratica chiusa',
                icon: XMarkIcon,
                obbligatoria: false,
                status: 'available'
              },
              {
                id: 'richiedi_integrazione_valutazione',
                label: 'Richiedi integrazione → Richiesta integrazione',
                icon: ExclamationTriangleIcon,
                obbligatoria: false,
                status: 'available'
              }
            ]
          };
        }
        break;

      case 'attesa_firma_contratto':
        if (ruoloUtente === 'admin') {
          return {
            titolo: '7. Attesa Firma Contratto - Workflow Amministratore',
            descrizione: 'Carica contratto da far sottoscrivere al cliente.',
            azioni: [
              {
                id: 'carica_contratto',
                label: 'Carica contratto da far sottoscrivere',
                icon: CloudArrowUpIcon,
                obbligatoria: true,
                status: 'pending'
              },
              {
                id: 'modifica_contratto',
                label: 'Modifica/Sostituisci contratto caricato',
                icon: PencilIcon,
                obbligatoria: false,
                status: 'available'
              },
              {
                id: 'attiva_contratto',
                label: 'Attiva contratto (se firmato dal cliente)',
                icon: CheckCircleIcon,
                obbligatoria: false,
                status: 'available'
              }
            ]
          };
        } else if (ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale') {
          return {
            titolo: '7. Attesa Firma Contratto - Workflow Agenzia',
            descrizione: 'Scarica contratto, fallo firmare al cliente e carica quello firmato.',
            azioni: [
              {
                id: 'scarica_contratto',
                label: 'Scarica contratto allegato dall\'amministratore',
                icon: EyeIcon,
                obbligatoria: false,
                status: 'available'
              },
              {
                id: 'carica_contratto_firmato',
                label: 'Allega contratto firmato dal cliente',
                icon: CloudArrowUpIcon,
                obbligatoria: true,
                status: 'pending'
              }
            ]
          };
        }
        break;

      case 'contratto_attivo':
        return {
          titolo: '8. Contratto Attivo - Pratica Completata',
          descrizione: 'Contratto verificato, firmato e attivo. Aggiornamento contratti e statistiche fatturato.',
          azioni: [
            {
              id: 'visualizza_contratto',
              label: 'Visualizza contratto attivo',
              icon: EyeIcon,
              obbligatoria: false,
              status: 'completed'
            },
            {
              id: 'aggiorna_statistiche',
              label: 'Aggiorna statistiche fatturato (automatico)',
              icon: CogIcon,
              obbligatoria: false,
              status: 'completed'
            }
          ]
        };

      case 'richiesta_integrazione':
        if (ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale') {
          return {
            titolo: 'Richiesta Integrazione - Workflow Agenzia',
            descrizione: 'Documenti mancanti o errati. Integra la documentazione.',
            azioni: [
              {
                id: 'visualizza_richieste',
                label: 'Visualizza richieste di integrazione',
                icon: EyeIcon,
                obbligatoria: true,
                status: 'pending'
              },
              {
                id: 'carica_integrazioni',
                label: 'Carica documenti di integrazione',
                icon: CloudArrowUpIcon,
                obbligatoria: true,
                status: 'pending'
              }
            ]
          };
        }
        break;

      case 'pratica_chiusa':
        return {
          titolo: 'Pratica Chiusa',
          descrizione: 'Pratica rifiutata e chiusa definitivamente.',
          azioni: [
            {
              id: 'visualizza_motivo',
              label: 'Visualizza motivo chiusura',
              icon: EyeIcon,
              obbligatoria: false,
              status: 'completed'
            }
          ]
        };

      case 'esito_negativo':
        return {
          titolo: 'Esito Negativo',
          descrizione: 'Pratica chiusa con esito negativo (operazione non andata a buon fine).',
          azioni: [
            {
              id: 'visualizza_motivo',
              label: 'Visualizza motivazione esito negativo',
              icon: EyeIcon,
              obbligatoria: false,
              status: 'completed'
            }
          ]
        };

      case 'condizionata':
        return {
          titolo: 'Pratica Condizionata',
          descrizione: 'Approvazione condizionata. Dalla tab Riepilogo l\'amministratore può proseguire verso il contratto o chiudere con esito negativo.',
          azioni: [
            {
              id: 'visualizza_condizioni',
              label: 'Visualizza condizioni nello storico',
              icon: EyeIcon,
              obbligatoria: false,
              status: 'available'
            }
          ]
        };

      case 'attesa_approvazione_cliente':
        // Stato legacy - mappiamo alle stesse azioni di valutazione_cliente
        if (ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale') {
          return {
            titolo: 'Attesa Approvazione Cliente',
            descrizione: 'Il cliente deve valutare i preventivi inviati e confermare la volontà di procedere.',
            azioni: [
              {
                id: 'conferma_cliente',
                label: 'Cliente approva e conferma preventivo/i',
                icon: CheckCircleIcon,
                obbligatoria: true,
                status: 'pending'
              },
              {
                id: 'modifica_richiesta',
                label: 'Modifica preventivo per esigenze cliente',
                icon: PencilIcon,
                obbligatoria: false,
                status: 'available'
              },
              {
                id: 'richiedi_ulteriori',
                label: 'Richiedi preventivi aggiuntivi',
                icon: DocumentTextIcon,
                obbligatoria: false,
                status: 'available'
              },
              {
                id: 'carica_documenti_anticipati',
                label: 'Carica documenti in anticipo (opzionale)',
                icon: CloudArrowUpIcon,
                obbligatoria: false,
                status: 'available'
              }
            ]
          };
        }
        break;

      default:
        return null;
    }
    
    return null;
  };

  // Implementazione azione conferma_cliente con gestione preventivi multipli
  const handleConfermaClienteConPreventivi = async (preventiviApprovati) => {
    if (!preventiviApprovati || preventiviApprovati.length === 0) {
      toast.error('Devi selezionare almeno un preventivo approvato dal cliente');
      return;
    }

    try {
      setLoadingAvanzaStato(true);
      
      const statoAttuale = pratica.statoPratica || pratica.stato;
      
      // Se siamo in valutazione_cliente o attesa_approvazione_cliente, usiamo l'endpoint conferma-cliente specifico
      if (statoAttuale === 'valutazione_cliente' || statoAttuale === 'attesa_approvazione_cliente') {
        const response = await api.put(`/pratica/${pratica.id}/conferma-cliente`, {
          note: 'Cliente confermato tramite workflow guidato con preventivi selezionati',
          preventivi: preventiviApprovati
        }, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data && response.data.message) {
          const preventiviCount = preventiviApprovati.length;
          toast.success(
            preventiviCount === 1 
              ? 'Cliente confermato! 1 preventivo approvato, pratica avanzata ad "Attesa Documenti Cliente"'
              : `Cliente confermato! ${preventiviCount} preventivi approvati, pratica avanzata ad "Attesa Documenti Cliente"`
          );
          
          setShowConfermaClienteModal(false);
          setShowWorkflowModal(false);
          
          if (onStatoAggiornato) {
            onStatoAggiornato();
          }
        } else {
          throw new Error('Risposta non valida dal server');
        }
      } else {
        // Per altri stati usa l'endpoint approvazione-cliente
        const response = await api.put(`/pratica/${pratica.id}/approvazione-cliente`, {
          preventivi: preventiviApprovati,
          azione: 'approva'
        }, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.data && response.data.message) {
          const preventiviCount = preventiviApprovati.length;
          toast.success(
            preventiviCount === 1 
              ? 'Cliente confermato! 1 preventivo approvato, pratica avanzata ad "Attesa Documenti Cliente"'
              : `Cliente confermato! ${preventiviCount} preventivi approvati, pratica avanzata ad "Attesa Documenti Cliente"`
          );
          
          setShowConfermaClienteModal(false);
          setShowWorkflowModal(false);
          
          if (onStatoAggiornato) {
            onStatoAggiornato();
          }
        } else {
          throw new Error('Risposta non valida dal server');
        }
      }
      
    } catch (error) {
      console.error('Errore conferma cliente con preventivi:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Errore di connessione';
      toast.error(`Errore nella conferma cliente: ${errorMessage}`);
    } finally {
      setLoadingAvanzaStato(false);
    }
  };

  const canAvanzaStato = () => {
    if (
      ruoloUtente !== 'agente' &&
      ruoloUtente !== 'agente_commerciale' &&
      ruoloUtente !== 'agenzia'
    ) return false;
    const statiAvanzabiliAgente = [
      'pratica_avviata',
      'valutazione_cliente',
      'attesa_documenti_cliente'
    ];
    return statiAvanzabiliAgente.includes(pratica.statoPratica || pratica.stato);
  };

  const getProssimoStato = () => {
    const sequenzaStatiUfficiale = {
      // Workflow principale agente
      'pratica_avviata': 'attesa_preventivo',
      'valutazione_cliente': 'attesa_documenti_cliente',
      'attesa_documenti_cliente': 'richiesta_esito',
      
      // Workflow amministratore (non gestito dall'avanzamento automatico)
      // 'attesa_preventivo': 'valutazione_cliente',           // Admin elabora preventivo
      // 'richiesta_esito': 'pratica_in_valutazione',          // Admin controlla documenti
      // 'pratica_in_valutazione': 'attesa_firma_contratto',   // Admin approva
      // 'attesa_firma_contratto': 'contratto_attivo',         // Admin verifica firme
      
      // Stati legacy per compatibilità
      'attesa_approvazione_cliente': 'attesa_documenti_cliente'
    };
    
    const statoCorrente = pratica.statoPratica || pratica.stato;
    return sequenzaStatiUfficiale[statoCorrente] || null;
  };

  const handleAvanzaStato = async () => {
    try {
      const prossimoStato = getProssimoStato();
      if (!prossimoStato) return;

      setLoadingAvanzaStato(true);

      const response = await api.put(`/pratica/${pratica.id}/stato`, {
        nuovoStato: prossimoStato,
        note: `Stato avanzato automaticamente da ${pratica.statoPratica || pratica.stato} a ${prossimoStato}`
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.message) {
        toast.success(`Stato aggiornato a: ${getStatoInfo(prossimoStato).label}`);
        onStatoAggiornato();
      }
    } catch (error) {
      console.error('Errore durante l\'avanzamento dello stato:', error);
      
      // Gestione specifica errori documenti mancanti
      if (error.response?.status === 400 && error.response?.data?.documentiMancanti) {
        const errorData = error.response.data;
        toast.error(
          <div>
            <div className="font-semibold">Documenti mancanti!</div>
            <div className="text-sm mt-1">
              {errorData.message || `Mancano ${errorData.documentiMancanti.length} documenti obbligatori`}
            </div>
            <div className="text-xs mt-2 opacity-75">
              Vai alla tab Documenti per completare il caricamento
            </div>
          </div>,
          { autoClose: 8000 }
        );
      } else {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Errore durante l\'avanzamento dello stato';
        toast.error(errorMessage);
      }
    } finally {
      setLoadingAvanzaStato(false);
    }
  };

  const handleCambioStato = async (nuovoStato) => {
    try {
      const response = await api.put(`/pratica/${pratica.id}/stato`, {
        nuovoStato: nuovoStato,
        note: `Stato cambiato manualmente a ${nuovoStato}`
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.message) {
        toast.success(`Stato aggiornato a: ${getStatoInfo(nuovoStato).label}`);
        setShowStateModal(false);
        onStatoAggiornato();
      }
    } catch (error) {
      console.error('Errore durante il cambio stato:', error);
      toast.error('Errore durante il cambio stato');
    }
  };

  // Stati finali in cui l'esito negativo non è più applicabile
  const STATI_TERMINALI = ['esito_negativo', 'pratica_chiusa', 'completata', 'annullata', 'rifiutata'];
  const isPraticaAttiva = () => !STATI_TERMINALI.includes(pratica.statoPratica || pratica.stato);

  const handleEsitoNegativo = async () => {
    if (!motivazioneEsitoNegativo.trim()) {
      toast.error('Inserisci una motivazione per l\'esito negativo');
      return;
    }
    try {
      setLoadingEsitoNegativo(true);
      const response = await api.put(`/pratica/${pratica.id}/esito-negativo`, {
        motivazione: motivazioneEsitoNegativo.trim()
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.data && response.data.success) {
        toast.success('Pratica chiusa con esito negativo');
        setShowEsitoNegativoModal(false);
        setMotivazioneEsitoNegativo('');
        onStatoAggiornato();
      }
    } catch (error) {
      console.error('Errore durante la registrazione dell\'esito negativo:', error);
      toast.error(error.response?.data?.error || 'Errore durante la registrazione dell\'esito negativo');
    } finally {
      setLoadingEsitoNegativo(false);
    }
  };

  const handleAttivaContratto = async () => {
    try {
      setLoadingAvanzaStato(true);
      
      const response = await api.put(`/pratica/${pratica.id}/attiva-contratto`, {
        note: 'Contratto attivato dall\'amministratore'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.success) {
        toast.success(
          <div>
            <div className="font-semibold">Contratto Attivato!</div>
            <div className="text-sm mt-1">
              Contratto inserito nel database e statistiche fatturato aggiornate
            </div>
          </div>,
          { autoClose: 5000 }
        );
        
        setShowWorkflowModal(false);
        
        if (onStatoAggiornato) {
          onStatoAggiornato();
        }
      } else {
        throw new Error('Risposta non valida dal server');
      }
      
    } catch (error) {
      console.error('Errore attivazione contratto:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Errore durante l\'attivazione del contratto';
      toast.error(`Errore attivazione contratto: ${errorMessage}`);
    } finally {
      setLoadingAvanzaStato(false);
    }
  };

  const statoInfo = getStatoInfo(pratica.statoPratica || pratica.stato);
  const workflow = getWorkflowPerStato();

  // Esponi metodi per l'uso esterno tramite ref
  useImperativeHandle(ref, () => ({
    apriModalConfermaCliente: () => {
      // Verifica che la pratica sia negli stati giusti
      const statoAttuale = pratica?.statoPratica || pratica?.stato;
      if (statoAttuale === 'valutazione_cliente' || statoAttuale === 'attesa_approvazione_cliente') {
        setShowConfermaClienteModal(true);
      } else {
        toast.error(`Azione non disponibile per lo stato "${statoAttuale}"`);
      }
    }
  }), [pratica]);

  return (
    <div className="flex items-center space-x-3">
      {/* Visualizzazione Stato Corrente */}
      <div className="flex items-center">
        <span className="font-medium text-gray-700">Stato:</span> 
        <span className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statoInfo.color}`}>
          <statoInfo.IconComp className="h-3.5 w-3.5 mr-1" />
          {statoInfo.label}
        </span>
      </div>

      {/* Pulsante Workflow (per stato Pratica Avviata) */}
      {workflow && (
        <button
          onClick={() => setShowWorkflowModal(true)}
          className="px-4 py-2 rounded-lg flex items-center bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
        >
          <ClipboardDocumentListIcon className="h-4 w-4 mr-2" />
          Workflow Guidato
        </button>
      )}
      
      {/* Pulsante Avanza Stato (per agenti, agenzia, agente commerciale) */}
      {(ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale' || ruoloUtente === 'agenzia') && canAvanzaStato() && (
        <button
          onClick={handleAvanzaStato}
          disabled={loadingAvanzaStato || !isPraticaModificabileAgente()}
          className={`px-4 py-2 rounded-lg flex items-center transition-colors ${
            loadingAvanzaStato || !isPraticaModificabileAgente()
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700'
          }`}
          title={`Avanza a: ${getStatoInfo(getProssimoStato()).label}`}
        >
          {loadingAvanzaStato ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
          ) : (
            <ArrowRightIcon className="h-4 w-4 mr-2" />
          )}
          Avanza Stato
        </button>
      )}
      
      {/* Pulsante Cambia Stato (solo per admin) */}
      {ruoloUtente === 'admin' && (
        <button
          onClick={isPraticaModificabileAgente() ? () => setShowStateModal(true) : undefined}
          className={`px-4 py-2 rounded-lg flex items-center transition-colors ${
            isPraticaModificabileAgente()
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          disabled={!isPraticaModificabileAgente()}
          title={!isPraticaModificabileAgente() ? 'Non disponibile durante la revisione' : 'Cambia stato pratica'}
        >
          <PencilIcon className="h-4 w-4 mr-2" />
          Cambia Stato
        </button>
      )}

      {/* Pulsante Esito Negativo - disponibile da qualsiasi stato attivo */}
      {isPraticaAttiva() && ['admin', 'agenzia', 'agente', 'agente_commerciale', 'team_leader'].includes(ruoloUtente) && (
        <button
          onClick={() => setShowEsitoNegativoModal(true)}
          className="px-4 py-2 rounded-lg flex items-center transition-colors bg-rose-600 text-white hover:bg-rose-700"
          title="Chiudi la pratica con esito negativo"
        >
          <XCircleIcon className="h-4 w-4 mr-2" />
          Esito Negativo
        </button>
      )}

      {/* Modal Esito Negativo */}
      {showEsitoNegativoModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="flex items-center mb-4">
              <XCircleIcon className="h-6 w-6 text-rose-600 mr-2" />
              <h3 className="text-lg font-bold text-gray-900">Esito Negativo Pratica</h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              La pratica verrà chiusa con esito negativo. L'operazione è definitiva: indica la motivazione.
            </p>
            <textarea
              value={motivazioneEsitoNegativo}
              onChange={(e) => setMotivazioneEsitoNegativo(e.target.value)}
              rows={4}
              placeholder="Motivazione dell'esito negativo (obbligatoria)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-rose-500 focus:border-rose-500 mb-4"
            />
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => { setShowEsitoNegativoModal(false); setMotivazioneEsitoNegativo(''); }}
                disabled={loadingEsitoNegativo}
                className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleEsitoNegativo}
                disabled={loadingEsitoNegativo || !motivazioneEsitoNegativo.trim()}
                className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingEsitoNegativo ? 'Salvataggio...' : 'Conferma Esito Negativo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Workflow Guidato */}
      {showWorkflowModal && workflow && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-2/3 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-gray-900">{workflow.titolo}</h3>
              <p className="text-gray-600 mt-2">{workflow.descrizione}</p>
            </div>
            
            {/* Lista Azioni */}
            <div className="space-y-4">
              {workflow.azioni.map((azione, index) => (
                <div 
                  key={azione.id}
                  className={`p-4 rounded-lg border-2 ${
                    azione.obbligatoria 
                      ? 'border-orange-200 bg-orange-50' 
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                        azione.status === 'completed' ? 'bg-green-500 text-white' :
                        azione.status === 'error' ? 'bg-red-500 text-white' :
                        'bg-gray-300 text-gray-600'
                      }`}>
                        {azione.status === 'completed' ? '✓' : 
                         azione.status === 'error' ? '✗' : index + 1}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 flex items-center">
                          <azione.icon className="h-4 w-4 mr-2" />
                          {azione.label}
                          {azione.obbligatoria && (
                            <span className="ml-2 text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                              Obbligatoria
                            </span>
                          )}
                        </h4>
                      </div>
                    </div>
                    
                    {/* Pulsanti Azione */}
                    <div className="flex space-x-2">
                      {azione.id === 'verifica_anagrafica' && (
                        <button 
                          onClick={() => {
                            setShowWorkflowModal(false);
                            onStatoAggiornato('anagrafica'); // Naviga alla tab anagrafica
                          }}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                        >
                          Vai ad Anagrafica
                        </button>
                      )}
                      {azione.id === 'modifica_anagrafica' && (
                        <button 
                          onClick={() => {
                            setShowWorkflowModal(false);
                            onStatoAggiornato('anagrafica');
                          }}
                          className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                        >
                          Vai ad Anagrafica
                        </button>
                      )}
                      {azione.id === 'seleziona_offerta' && (
                        <button 
                          onClick={() => {
                            setShowWorkflowModal(false);
                            onStatoAggiornato('offerte'); // Naviga alla tab offerte
                          }}
                          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                        >
                          Vai a Offerte
                        </button>
                      )}
                      {azione.id === 'richiedi_preventivo_personalizzato' && (
                        <button 
                          onClick={() => {
                            setShowWorkflowModal(false);
                            onStatoAggiornato('preventivi'); // Naviga alla tab preventivi
                          }}
                          className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                        >
                          Vai a Preventivi
                        </button>
                      )}
                      {azione.id === 'modifica_preventivo' && (
                        <button 
                          onClick={() => {
                            setShowWorkflowModal(false);
                            onStatoAggiornato('preventivi');
                          }}
                          className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                        >
                          Vai a Preventivi
                        </button>
                      )}
                      {azione.id === 'carica_documenti' && (
                        <button 
                          onClick={() => {
                            setShowWorkflowModal(false);
                            onStatoAggiornato('documenti'); // Naviga alla tab documenti
                          }}
                          className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
                        >
                          Vai a Documenti
                        </button>
                      )}
                      {azione.id === 'sostituisci_documenti' && (
                        <button 
                          onClick={() => {
                            setShowWorkflowModal(false);
                            onStatoAggiornato('documenti');
                          }}
                          className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
                        >
                          Gestisci Documenti
                        </button>
                      )}
                      {azione.id === 'conferma_cliente' && (
                        <button 
                          onClick={() => {
                            setShowConfermaClienteModal(true);
                          }}
                          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                        >
                          Conferma Cliente
                        </button>
                      )}
                      {azione.id === 'avanza_documenti' && (
                        <button 
                          onClick={() => {
                            handleCambioStato('attesa_documenti_cliente');
                            setShowWorkflowModal(false);
                          }}
                          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                        >
                          Avanza Stato
                        </button>
                      )}
                      {azione.id === 'attiva_contratto' && (
                        <button 
                          onClick={handleAttivaContratto}
                          className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                        >
                          Attiva Contratto
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pulsante Attiva Contratto per Admin */}
            {pratica.statoPratica === 'attesa_firma_contratto' && ruoloUtente === 'admin' && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-semibold text-green-800 mb-2 flex items-center">
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      Attivazione Contratto
                    </h4>
                    <p className="text-green-700 text-sm mb-3">
                      Il contratto è pronto per essere attivato. Questa azione creerà il contratto nel database 
                      e aggiornerà le statistiche fatturato.
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={handleAttivaContratto}
                  disabled={loadingAvanzaStato}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  {loadingAvanzaStato ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Attivazione in corso...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-5 w-5" />
                      Attiva Contratto
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Pulsanti Modal */}
            <div className="mt-6 flex justify-between">
              <div className="text-sm text-gray-500">
                <InformationCircleIcon className="h-4 w-4 inline mr-1" />
                <strong>Suggerimento:</strong> Completa le azioni obbligatorie per procedere con la pratica
              </div>
              <button
                onClick={() => setShowWorkflowModal(false)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Conferma Cliente */}
      {showConfermaClienteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <ConfermaClienteConPreventivi 
                idPratica={pratica.id}
                onConferma={handleConfermaClienteConPreventivi}
                onAnnulla={() => setShowConfermaClienteModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Cambio Stato */}
      {showStateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-6 border w-full max-w-5xl shadow-lg rounded-lg bg-white">
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900 flex items-center">
                  <ArrowPathIcon className="h-5 w-5 mr-2 text-gray-600" />
                  Cambia Stato Pratica
                </h3>
                <button
                  onClick={() => setShowStateModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Seleziona il nuovo stato per la pratica <strong>{pratica.codicePratica}</strong>
              </p>
            </div>

            {/* Workflow Stati Principali */}
            <div className="space-y-6">
              {/* Workflow Principale */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                  Workflow Principale
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['pratica_avviata', 'attesa_preventivo', 'valutazione_cliente', 'attesa_documenti_cliente'].map(stato => {
                    const info = getStatoInfo(stato);
                    const IconStato = info.IconComp;
                    return (
                      <div
                        key={stato}
                        onClick={() => handleCambioStato(stato)}
                        className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg ${
                          pratica.statoPratica === stato 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        {pratica.statoPratica === stato && (
                          <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                            Attuale
                          </div>
                        )}
                        <div className="text-center flex flex-col items-center">
                          <IconStato className={`h-8 w-8 mb-2 ${info.iconColor}`} />
                          <div className="text-sm font-medium text-gray-900">{info.label}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Workflow Amministratore */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                  Workflow Amministratore
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['richiesta_esito', 'pratica_in_valutazione', 'attesa_firma_contratto', 'contratto_attivo'].map(stato => {
                    const info = getStatoInfo(stato);
                    const IconStato = info.IconComp;
                    return (
                      <div
                        key={stato}
                        onClick={() => handleCambioStato(stato)}
                        className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg ${
                          pratica.statoPratica === stato 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-blue-300'
                        }`}
                      >
                        {pratica.statoPratica === stato && (
                          <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                            Attuale
                          </div>
                        )}
                        <div className="text-center flex flex-col items-center">
                          <IconStato className={`h-8 w-8 mb-2 ${info.iconColor}`} />
                          <div className="text-sm font-medium text-gray-900">{info.label}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Stati Speciali */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                  Stati Speciali
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['richiesta_integrazione', 'pratica_chiusa'].map(stato => {
                    const info = getStatoInfo(stato);
                    const IconStato = info.IconComp;
                    return (
                      <div
                        key={stato}
                        onClick={() => handleCambioStato(stato)}
                        className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg ${
                          pratica.statoPratica === stato 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-red-300'
                        }`}
                      >
                        {pratica.statoPratica === stato && (
                          <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                            Attuale
                          </div>
                        )}
                        <div className="text-center flex flex-col items-center">
                          <IconStato className={`h-8 w-8 mb-2 ${info.iconColor}`} />
                          <div className="text-sm font-medium text-gray-900">{info.label}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Pulsanti Azione */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowStateModal(false)}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default GestoreStatiPratica; 